import React, { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const RealityGarden = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [affirmations, setAffirmations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [expandedImageIndex, setExpandedImageIndex] = useState(null);

  const startTuneUp = () => {
    if (!user?.affirmations || user.affirmations.length === 0) {
      alert('You need to set up your personal affirmations first. Go to your Profile → Affirmations to get started!');
      return;
    }
    // Navigate to Daily Ritual with bypass parameter to avoid RitualGuard conflicts
    navigate('/daily-ritual?bypass=true');
  };

  const shuffleArray = (array) => {
    const shuffled = [...array];
    for (let i = shuffled.length - 1; i > 0; i--) {
      const j = Math.floor(Math.random() * (i + 1));
      [shuffled[i], shuffled[j]] = [shuffled[j], shuffled[i]];
    }
    return shuffled;
  };

  const expandImage = (index) => {
    setExpandedImageIndex(index);
  };

  const closeExpandedImage = () => {
    setExpandedImageIndex(null);
  };

  useEffect(() => {
    fetchCommunityAffirmations();
  }, []);

  const fetchCommunityAffirmations = async () => {
    try {
      const token = localStorage.getItem('auth_token');
      if (!token) {
        console.log('🌱 No auth token found - user needs to log in');
        setLoading(false);
        return;
      }

      const headers = { Authorization: `Bearer ${token}` };
      const response = await axios.get(`${API}/community-affirmations`, { headers });
      
      // Randomize the affirmations
      const shuffledAffirmations = shuffleArray(response.data.affirmations || []);
      setAffirmations(shuffledAffirmations);
      
      console.log('🌸 Fetched and randomized community affirmations:', shuffledAffirmations.length);
    } catch (error) {
      console.error('Error fetching community affirmations:', error);
      
      // Enhanced error handling
      if (error.response?.status === 401) {
        console.log('🌱 Authentication failed - clearing local storage');
        localStorage.removeItem('auth_token');
        localStorage.removeItem('auth_user');
        setAffirmations([]);
      } else {
        console.log('🌱 Other error occurred:', error.message);
        setAffirmations([]);
      }
    } finally {
      setLoading(false);
    }
  };

  // Expanded Image Modal
  if (expandedImageIndex !== null && affirmations[expandedImageIndex]?.first_vision_image) {
    return (
      <div 
        className="fixed inset-0 bg-black bg-opacity-90 flex items-center justify-center z-50 p-4" 
        onClick={closeExpandedImage}
      >
        <div className="max-w-3xl max-h-3xl">
          <img 
            src={affirmations[expandedImageIndex].first_vision_image} 
            alt="Vision Board" 
            className="max-w-full max-h-full object-contain rounded-lg shadow-2xl"
          />
        </div>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-emerald-50 to-teal-100 flex items-center justify-center">
        <div className="text-center">
          <div className="w-16 h-16 border-4 border-emerald-300 border-t-emerald-600 rounded-full animate-spin mx-auto"></div>
          <p className="mt-4 text-emerald-700 font-medium">Growing the Reality Garden...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-emerald-50 to-teal-100">
      {/* Hero Section */}
      <div 
        className="relative py-20 px-4"
        style={{
          backgroundImage: `linear-gradient(rgba(0,0,0,0.4), rgba(0,0,0,0.6)), url('https://images.unsplash.com/photo-1528715471579-d1bcf0ba5e83')`,
          backgroundSize: 'cover',
          backgroundPosition: 'center'
        }}
      >
        <div className="max-w-4xl mx-auto text-center text-white">
          <h1 className="text-5xl font-bold mb-6">🌱 Reality Garden</h1>
          <p className="text-xl mb-8 text-gray-200 max-w-2xl mx-auto">
            Immerse yourself in the collective wisdom of our community. Here you'll find randomized affirmations 
            from fellow consciousness explorers, sharing their intentions and positive energy.
          </p>
          <div className="flex justify-center">
            <button
              onClick={startTuneUp}
              className="px-8 py-4 bg-gradient-to-r from-purple-500 to-pink-500 text-white rounded-full font-semibold text-lg hover:shadow-2xl transform hover:scale-105 transition-all duration-300"
            >
              🧘 Tune-Up
            </button>
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className="max-w-6xl mx-auto px-4 py-12">
        {/* Stats Section */}
        <div className="grid md:grid-cols-3 gap-6 mb-12">
          <div className="bg-white rounded-xl shadow-lg p-6 text-center">
            <div className="text-3xl mb-2">🌸</div>
            <h3 className="text-2xl font-bold text-emerald-600">{affirmations.length}</h3>
            <p className="text-gray-600">Community Affirmations</p>
          </div>
          <div className="bg-white rounded-xl shadow-lg p-6 text-center">
            <div className="text-3xl mb-2">🌿</div>
            <h3 className="text-2xl font-bold text-emerald-600">∞</h3>
            <p className="text-gray-600">Positive Energy</p>
          </div>
          <div className="bg-white rounded-xl shadow-lg p-6 text-center">
            <div className="text-3xl mb-2">✨</div>
            <h3 className="text-2xl font-bold text-emerald-600">24/7</h3>
            <p className="text-gray-600">Always Growing</p>
          </div>
        </div>

        {/* Affirmations Grid - Tall Post Style with Vision Board Images */}
        {affirmations.length > 0 ? (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 max-w-5xl mx-auto">
            {affirmations.map((affirmation, index) => (
              <div key={index} className="bg-white rounded-xl shadow-lg overflow-hidden hover:shadow-xl transition-shadow">
                
                {/* Vision Board Image - First image from user's vision board */}
                {affirmation.first_vision_image && (
                  <div 
                    className="h-64 bg-gray-100 overflow-hidden cursor-pointer"
                    onClick={() => expandImage(index)}
                  >
                    <img 
                      src={affirmation.first_vision_image} 
                      alt="Vision Board" 
                      className="w-full h-full object-cover hover:scale-105 transition-transform duration-300"
                    />
                  </div>
                )}
                
                {/* Affirmation Content */}
                <div className="p-6">
                  {/* Affirmation Text */}
                  <blockquote className="text-lg font-medium text-gray-800 mb-4 leading-relaxed italic">
                    "{affirmation.text}"
                  </blockquote>
                  
                  {/* Mystery Name */}
                  <div className="flex items-center justify-between">
                    <div className="flex items-center space-x-2">
                      <div className="w-8 h-8 bg-gradient-to-br from-emerald-400 to-teal-500 rounded-full flex items-center justify-center">
                        <span className="text-white text-sm">🌸</span>
                      </div>
                      <span className="text-sm font-semibold text-gray-600">
                        {affirmation.secret_mystery_name || 'Anonymous Dreamer'}
                      </span>
                    </div>
                    <div className="text-xs text-emerald-600 bg-emerald-50 px-2 py-1 rounded-full">
                      Affirmation
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        ) : (
          <div className="text-center py-12">
            <div className="text-6xl mb-4">🌸</div>
            <h3 className="text-2xl font-semibold text-gray-800 mb-4">Welcome to the Reality Garden</h3>
            <p className="text-gray-600 mb-6 max-w-md mx-auto">
              {localStorage.getItem('auth_token') 
                ? "No community affirmations are available right now. Check back later as our community shares their positive intentions."
                : "Please log in to access the community affirmations and share in the collective wisdom of our consciousness explorers."
              }
            </p>
            {!localStorage.getItem('auth_token') && (
              <button
                onClick={() => navigate('/login')}
                className="px-6 py-3 bg-gradient-to-r from-emerald-500 to-teal-600 text-white rounded-lg font-medium hover:shadow-lg transition-shadow"
              >
                🔑 Sign In to Join the Garden
              </button>
            )}
          </div>
        )}

        {/* Refresh Button */}
        <div className="text-center mt-12">
          <button
            onClick={fetchCommunityAffirmations}
            className="px-6 py-3 bg-gradient-to-r from-emerald-500 to-teal-600 text-white rounded-lg font-medium hover:shadow-lg transition-shadow"
          >
            🔄 Refresh Affirmations
          </button>
        </div>
      </div>
    </div>
  );
};

export default RealityGarden;